"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Phone, Mail, MapPin, MessageSquare, CircleCheck } from "lucide-react";
const contactDetails = [{
  id: "phone",
  icon: Phone,
  label: "מוקד זמין עכשיו",
  value: "072-330-5555"
}, {
  id: "email",
  icon: Mail,
  label: 'דוא"ל לתיאום שירות',
  value: "office@siudy.co.il"
}, {
  id: "address",
  icon: MapPin,
  label: "פריסה ארצית",
  value: "שירות מהיר בבית המטופל או בבתי החולים בכל רחבי הארץ"
}];
function InnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="207102" className="flex flex-col items-center justify-center py-12 px-6 text-center bg-accent/20 border border-border rounded-xl">
        <CircleCheck className="size-16 text-secondary mb-4" />
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">
          {successMessage}
        </h3>
        <p className="text-muted-foreground mb-6 font-default">
          פנייתך התקבלה. נציג שירות יחזור אליך טלפונית תוך דקות ספורות לתיאום המטפל.
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default rounded-full px-8">
          שליחת פנייה נוספת
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "שדה זה הוא חובה"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-primary font-medium">שם המטופל או איש הקשר</FormLabel>
              <FormControl>
                <Input placeholder="לדוגמה: ישראל ישראלי" className="bg-background border-input focus:ring-ring rounded-lg h-12" {...field} />
              </FormControl>
              <FormDescription className="text-xs">
                השם שבו נשתמש בשיחת הטלפון
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "שדה זה הוא חובה",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "מספר טלפון לא תקין"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-primary font-medium">טלפון לחזרה מיידית</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="05X-XXXXXXX" dir="ltr" className="text-right bg-background border-input focus:ring-ring rounded-lg h-12" {...field} />
              </FormControl>
              <FormDescription className="text-xs">
                מספר שזמין כרגע לקבלת שיחה
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'כתובת דוא"ל לא תקינה'
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-primary font-medium">כתובת דוא"ל (אופציונלי)</FormLabel>
            <FormControl>
              <Input type="email" placeholder="name@example.com" dir="ltr" className="text-right bg-background border-input focus:ring-ring rounded-lg h-12" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "שדה זה הוא חובה"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-primary font-medium">סוג השירות הנדרש ומיקום</FormLabel>
            <FormControl>
              <Textarea placeholder="לדוגמה: השגחה בבית חולים איכילוב / מטפל לילה בבית בחיפה" className="min-h-[120px] resize-y bg-background border-input focus:ring-ring rounded-lg" {...field} />
            </FormControl>
            <FormDescription className="text-xs">
              ציינו את המיקום המדויק ודחיפות השירות
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] font-default text-lg font-semibold h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all active:scale-95">
          {isSubmitting ? <>
              <Loader className="animate-spin ml-2 size-5" />
              מעבד בקשה...
            </> : <>
              <MessageSquare className="ml-2 size-5" />
              קבלת מענה מיידי
            </>}
        </Button>
        <span className="text-muted-foreground font-default text-sm hidden sm:block">או חייגו עכשיו: 072-330-5555</span>
      </div>
    </Form>;
}
export default function ContactMessageForm() {
  return <section id="השאירו-פרטים-ונחזור-תוך-דקות" className="py-16 md:py-24 bg-background text-foreground" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold mb-6 border border-secondary/20">
                <span className="relative flex h-2 w-2 ml-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
                </span>
                זמינות מיידית - פריסה ארצית
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-bold text-primary mb-6 leading-tight">
                צריכים מטפל עכשיו? <br />
                אנחנו בדרך אליכם.
              </h2>
              <p className="text-muted-foreground text-xl leading-relaxed font-default">
                אל תשארו לבד ברגעים הקריטיים. השאירו פרטים ותוך דקות מנהל שירות מקצועי יחזור אליכם לתיאום מטפל סיעודי ישראלי, מיומן ואנושי.
              </p>
            </div>

            <div className="space-y-6 bg-accent/10 p-8 rounded-2xl border border-border/50 backdrop-blur-sm">
              <h3 className="font-default font-bold text-2xl text-primary mb-4">
                פרטי התקשרות מהירה
              </h3>
              <ul className="space-y-6">
                {contactDetails.map((detail, i) => <li key={detail.id} data-index={i} className="flex items-start gap-5">
                    <div className="bg-primary p-3 rounded-xl shrink-0 mt-1 shadow-sm">
                      <detail.icon className="size-6 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-primary/70 mb-1 font-default">
                        {detail.label}
                      </p>
                      <p className="text-foreground font-semibold text-lg font-default" dir={detail.id === 'phone' || detail.id === 'email' ? 'ltr' : 'rtl'} style={{
                    textAlign: 'right'
                  }}>
                        {detail.value}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded-2xl shadow-xl p-6 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-2 h-full bg-secondary"></div>
              <div className="mb-8">
                <h3 className="text-2xl font-default font-bold text-primary mb-2">טופס פנייה דחופה</h3>
                <p className="text-muted-foreground font-default">מלאו את הפרטים לקבלת הצעת מחיר וזמינות מטפל</p>
              </div>
              <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="ContactMessageForm" defaultValues={{
              name: "",
              phone: "",
              email: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              email: {
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="קבלת מענה מיידי" successMessage="פנייתך התקבלה בהצלחה!" errorMessage="אירעה שגיאה. נסו שוב או התקשרו למוקד." className="w-full">
                <InnerForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}